import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  MessageSquare,
  MessageCircle,
  Search,
  FileText,
  Check,
  AlertCircle,
  Zap,
  DollarSign,
  Bot,
  Mail,
  Users,
  Lock,
} from "lucide-react";
import MainHeader from "../MainHeader";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

export default function DeepResearchQuestion() {
  const { api } = useAuth(); // baseURL already includes "/api/v1"
  const navigate = useNavigate();

  // 질문 방식, 검색어, 선택 파일, 총 용량, 페이지(다음으로 불러올 페이지), 파일 목록, 로딩/추가 가능 상태, subject
  const [selectedMode, setSelectedMode] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]); // { id, name, sizeMB }
  const [totalSize, setTotalSize] = useState(0); // in MB
  const [subject, setSubject] = useState("");
  const [files, setFiles] = useState([]); // loaded file objects
  const [nextPage, setNextPage] = useState(1);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [hasMoreFiles, setHasMoreFiles] = useState(true);

  const containerRef = useRef(null);
  const PAGE_SIZE = 30; // 백엔드 오프셋이 30

  // Human-readable 사이즈를 MB로 변환 ("2.4MB" or "856KB" 같은 문자열)
  const sizeToMB = (sizeStr) => {
    const s = sizeStr.toUpperCase().replace(/\s/g, "");
    if (s.endsWith("KB")) {
      return parseFloat(s.replace("KB", "")) / 1024;
    } else if (s.endsWith("MB")) {
      return parseFloat(s.replace("MB", ""));
    }
    return parseFloat(s) || 0;
  };

  // 페이지(pageParam)를 지정해 API 호출. reset=true면 기존 리스트 지우고 1페이지부터 시작
  const fetchFiles = async (pageParam, reset = false) => {
    if (isLoadingFiles) return;
    setIsLoadingFiles(true);
    try {
      const response = await api.get("/deep_research/fs/tablequery", {
        params: {
          sq: searchQuery,
          orderType: "date",
          p: pageParam,
        },
      });
      const viewFiles = response.data.data || [];
      // ViewDRFs 구조: { GormModel: { CreatedAt, ... }, Name, Size, Type }
      const mapped = viewFiles.map((f) => ({
        id: f.ID || f.id,
        name: f.Name || f.name,
        // Size는 "X.YMB" 또는 "ZKB" 형태
        sizeMB: sizeToMB(f.Size || f.size),
        uploadDate: f.CreatedAt
          ? new Date(f.CreatedAt).toISOString().split("T")[0]
          : "",
      }));
      if (reset) {
        setFiles(mapped);
        setNextPage(2);
        setHasMoreFiles(mapped.length === PAGE_SIZE);
      } else {
        setFiles((prev) => [...prev, ...mapped]);
        setNextPage(pageParam + 1);
        setHasMoreFiles(mapped.length === PAGE_SIZE);
      }
    } catch (err) {
      console.error("파일 목록 조회 중 오류:", err);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  // 검색어 변경 시 리스트 초기화, 1페이지부터 로드
  useEffect(() => {
    setFiles([]);
    setNextPage(1);
    setHasMoreFiles(true);
    fetchFiles(1, true);
  }, [searchQuery]);

  // 스크롤이 맨 밑에 가까워지면 다음 페이지 불러오기
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => {
      if (
        container.scrollHeight > container.clientHeight && // 내용이 컨테이너 높이보다 클 때만
        container.scrollTop + container.clientHeight >=
          container.scrollHeight - 50 && // 50px 내로 스크롤되면
        hasMoreFiles &&
        !isLoadingFiles
      ) {
        fetchFiles(nextPage, false);
      }
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [nextPage, hasMoreFiles, isLoadingFiles]);

  // 선택된 파일 toggle
  const handleFileSelect = (doc) => {
    const existing = selectedFiles.find((f) => f.id === doc.id);
    if (existing) {
      setSelectedFiles((prev) => prev.filter((f) => f.id !== doc.id));
      setTotalSize((prev) => +(prev - doc.sizeMB).toFixed(1));
    } else {
      if (totalSize + doc.sizeMB > 30) {
        alert("파일 크기 합계가 30MB를 초과할 수 없습니다.");
        return;
      }
      setSelectedFiles((prev) => [...prev, doc]);
      setTotalSize((prev) => +(prev + doc.sizeMB).toFixed(1));
    }
  };

  const canStartChat =
    selectedMode && selectedFiles.length > 0 && subject.trim().length > 0;

  // 채팅 시작: POST /deep_research/chat/start
  const handleStartChat = async () => {
    if (!canStartChat) return;
    const reqBody = {
      subject: subject,
      turn_model: selectedMode === "multi" ? "multi_turn" : "single_turn",
      documents: selectedFiles.map((f) => String(f.id)),
    };
    try {
      const response = await api.post("/deep_research/chat/start", reqBody);
      const sessionId = response.data.session;
      localStorage.setItem("deepResearchSession", sessionId);
      navigate(
        `/deep_research/chat?session=${sessionId}&qt=${
          selectedMode === "multi" ? "multi_turn" : "single_turn"
        }`
      );
    } catch (err) {
      console.error("채팅 준비 중 오류:", err);
      alert("채팅 시작 중 오류가 발생했습니다.");
    }
  };

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 relative overflow-hidden">
        {/* 배경 블러 + 그리드 패턴 */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-400/30 rounded-full filter blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-[500px] h-[500px] bg-purple-400/30 rounded-full filter blur-[120px] animate-pulse delay-700"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-400/20 rounded-full filter blur-[100px] animate-pulse delay-1000"></div>
          <div
            className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http://www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"
            opacity-20
          ></div>
        </div>

        <div className="relative z-10">
          {/* 헤더 */}
          <div className="px-6 py-8">
            <div className="max-w-6xl mx-auto">
              <button
                onClick={() => window.history.back()}
                className="group flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-6"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                돌아가기
              </button>

              <div className="flex items-center gap-4 mb-8">
                <div className="relative">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-lg rounded-2xl flex items-center justify-center">
                    <MessageSquare className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-2xl blur opacity-50"></div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">
                    문서 질의응답
                  </h1>
                  <p className="text-blue-100 mt-1">
                    질문 방식을 선택하고 분석할 문서를 선택하세요
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 컨텐츠 영역 */}
          <div className="max-w-6xl mx-auto px-6 pb-12 space-y-8">
            {/* 질문 방식 선택 */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">
                질문 방식 선택
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {/* 1회성 질문 */}
                <div
                  onClick={() => setSelectedMode("single")}
                  className={`group relative bg-white/10 backdrop-blur-lg rounded-2xl p-6 cursor-pointer transform transition-all hover:scale-[1.02] border-2 ${
                    selectedMode === "single"
                      ? "border-white/40 bg-white/20"
                      : "border-white/20 hover:border-white/30"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">
                        1회성 질문
                      </h3>
                      <div className="space-y-2 text-sm text-blue-100">
                        <div className="flex items-start gap-2">
                          <DollarSign className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-400" />
                          <span>비용이 저렴합니다.</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <Zap className="w-4 h-4 flex-shrink-0 mt-0.5 text-yellow-400" />
                          <span>한 번에 여러 질문이 가능합니다.</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-orange-400" />
                          <span>
                            이어서 대화가 불가능합니다. 메일 형태와 유사합니다.
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-300" />
                          <span>한 번의 요약 및 질문에 적합</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {selectedMode === "single" && (
                    <div className="absolute top-4 right-4">
                      <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-blue-600" />
                      </div>
                    </div>
                  )}
                </div>

                {/* 멀티턴 대화 (비활성화) */}
                <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl p-6 opacity-50 cursor-not-allowed border-2 border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gray-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Lock className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">
                        멀티턴 대화 (수리 중)
                      </h3>
                      <div className="space-y-2 text-sm text-gray-400">
                        <div className="flex items-start gap-2">
                          <DollarSign className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
                          <span>현재 내부 이슈로 이용 불가</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <MessageCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
                          <span>수리 완료 후 다시 이용해 주세요.</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
                          <span>
                            멀티턴 대화 기능은 빠른 시일 내 복구됩니다.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4">
                    <Lock className="w-6 h-6 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Subject 입력 */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">
                주제 입력
              </h2>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="질문 주제를 입력하세요"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:bg-white/20 focus:border-white/40 transition-all"
              />
            </div>

            {/* 파일 선택 영역 */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">
                분석할 문서 선택
              </h2>
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
                {/* 검색창 */}
                <div className="relative mb-6">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                  <input
                    type="text"
                    placeholder="문서 이름으로 검색..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-white/40 focus:bg-white/20 transition-all"
                  />
                </div>

                {/* 파일 리스트 - 스크롤 컨테이너 */}
                <div
                  ref={containerRef}
                  className="space-y-3 max-h-96 overflow-y-auto"
                >
                  {files.map((doc) => {
                    const isSelected = selectedFiles.some(
                      (f) => f.id === doc.id
                    );
                    return (
                      <div
                        key={doc.id}
                        onClick={() => handleFileSelect(doc)}
                        className={`p-4 rounded-xl cursor-pointer transition-all ${
                          isSelected
                            ? "bg-white/20 border-2 border-white/40"
                            : "bg-white/5 border-2 border-transparent hover:bg-white/10"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                isSelected
                                  ? "bg-white border-white"
                                  : "border-white/40"
                              }`}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-blue-600" />
                              )}
                            </div>
                            <FileText className="w-5 h-5 text-white/60" />
                            <div>
                              <p className="font-medium text-white">
                                {doc.name}
                              </p>
                              <p className="text-sm text-blue-200">
                                {doc.sizeMB.toFixed(1)}MB • {doc.uploadDate}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {isLoadingFiles && (
                    <div className="flex items-center justify-center py-4">
                      <p className="text-white/70">로딩 중...</p>
                    </div>
                  )}

                  {!hasMoreFiles && !isLoadingFiles && files.length > 0 && (
                    <div className="flex items-center justify-center py-4">
                      <p className="text-white/70">불러올 문서가 없습니다</p>
                    </div>
                  )}

                  {files.length === 0 && !isLoadingFiles && (
                    <div className="flex items-center justify-center py-4">
                      <p className="text-white/70">검색 결과가 없습니다</p>
                    </div>
                  )}
                </div>

                {/* 크기 제한 안내 */}
                <div className="mt-4 p-3 bg-white/5 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-blue-100">
                      최대 30MB까지 선택 가능
                    </span>
                  </div>
                  <span
                    className={`text-sm font-medium ${
                      totalSize > 25 ? "text-yellow-400" : "text-white"
                    }`}
                  >
                    {totalSize.toFixed(1)} / 30 MB
                  </span>
                </div>
              </div>
            </div>

            {/* 채팅 시작 버튼 */}
            <div className="flex justify-center">
              <button
                disabled={!canStartChat}
                onClick={handleStartChat}
                className={`px-8 py-4 rounded-xl font-medium text-lg transition-all transform ${
                  canStartChat
                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 hover:scale-105 shadow-lg"
                    : "bg-white/10 text-white/40 cursor-not-allowed"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Bot className="w-6 h-6" />
                  <span>채팅 시작</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
